import { Link, useLocation, useParams } from 'react-router-dom';
import type { Order } from '@harbor/shared';
import { useApi } from '../lib/hooks.js';
import { money, dateTime, titleCase, orderStatusTone } from '../lib/format.js';
import { Alert, Badge, Button, Card, EmptyState, Skeleton } from '../components/ui.js';

/**
 * Order confirmation.
 *
 * The order is taken from router state when the customer arrives straight from
 * checkout, and fetched otherwise. That dual path matters for guest checkout:
 * `GET /api/orders/:id` requires authentication, so a guest would see an error
 * on their own confirmation page if the component only knew how to fetch.
 * Passing the response through `navigate(..., { state })` is a single in-memory
 * hop and cannot leak, because it never touches the URL or storage.
 */
export function OrderConfirmation() {
  const { orderId } = useParams<{ orderId: string }>();
  const location = useLocation();
  const fromState = (location.state as { order?: Order } | null)?.order ?? null;

  const { data, loading } = useApi<{ order: Order }>(fromState ? null : `/api/orders/${orderId}`);
  const order = fromState ?? data?.order ?? null;

  if (loading && !order) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 px-4 py-16 sm:px-6">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 sm:px-6">
        <EmptyState
          title="We could not load that order"
          description="If you checked out as a guest, the confirmation is only available immediately after ordering."
          action={
            <Button variant="secondary" onClick={() => window.location.assign('/')}>
              Back to the catalogue
            </Button>
          }
        />
      </div>
    );
  }

  const paid = order.status === 'paid' || order.status === 'fulfilled';
  const cancelled = order.status === 'cancelled';

  return (
    <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6">
      <div className="text-center">
        <div
          className={[
            'mx-auto flex size-14 items-center justify-center rounded-full',
            paid ? 'bg-positive-soft text-positive' : cancelled ? 'bg-danger-soft text-danger' : 'bg-warning-soft text-warning',
          ].join(' ')}
          aria-hidden="true"
        >
          {paid ? <CheckIcon /> : cancelled ? <CrossIcon /> : <ClockIcon />}
        </div>

        <h1 className="mt-5 text-2xl font-semibold tracking-tight">
          {paid ? 'Order confirmed' : cancelled ? 'Order not completed' : 'Awaiting payment'}
        </h1>
        <p className="mt-2 text-sm text-muted">
          {paid
            ? 'A confirmation has been sent. Nothing will actually ship — this is a demonstration store.'
            : cancelled
              ? 'The payment did not go through, so no order was placed and no stock was held.'
              : 'The payment provider needs another step. The order is held for 30 minutes.'}
        </p>

        <div className="mt-4 flex items-center justify-center gap-2">
          <Badge tone={orderStatusTone(order.status) === 'positive' ? 'positive' : orderStatusTone(order.status) === 'danger' ? 'danger' : 'warning'}>
            {titleCase(order.status)}
          </Badge>
          <span className="tabular text-sm text-muted">{order.number}</span>
        </div>
      </div>

      {cancelled && (
        <div className="mt-8">
          <Alert tone="danger" title="Nothing was charged">
            <p>
              Your basket was preserved. You can return to it and try a different test card — try{' '}
              <code>tok_visa</code> to see the successful path.
            </p>
            <Link
              to="/"
              className="mt-3 inline-flex h-9 items-center rounded-lg border border-line bg-surface px-3 text-sm font-medium"
            >
              Back to the catalogue
            </Link>
          </Alert>
        </div>
      )}

      <Card className="mt-8 overflow-hidden">
        <div className="border-b border-line px-6 py-4">
          <h2 className="text-sm font-semibold">Order {order.number}</h2>
          <p className="mt-0.5 text-xs text-subtle">Placed {dateTime(order.placedAt)}</p>
        </div>

        <ul className="divide-y divide-line">
          {order.items.map((item) => (
            <li key={item.id} className="flex items-center gap-4 px-6 py-4">
              <div className="size-16 shrink-0 overflow-hidden rounded-lg border border-line bg-sunken">
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt="" className="size-full object-cover" />
                ) : null}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{item.name}</p>
                <p className="tabular text-xs text-subtle">
                  {item.quantity} × {money(item.unitPriceMinor, order.currency)}
                </p>
              </div>
              <span className="tabular text-sm">{money(item.lineTotalMinor, order.currency)}</span>
            </li>
          ))}
        </ul>

        <dl className="space-y-1.5 border-t border-line bg-raised px-6 py-4 text-sm">
          <Row label="Subtotal" value={money(order.subtotalMinor, order.currency)} />
          <Row
            label="Shipping"
            value={order.shippingMinor === 0 ? 'Free' : money(order.shippingMinor, order.currency)}
          />
          <Row label="Tax" value={money(order.taxMinor, order.currency)} />
          <div className="flex items-center justify-between border-t border-line pt-2 text-base font-semibold">
            <dt>Total</dt>
            <dd className="tabular">{money(order.totalMinor, order.currency)}</dd>
          </div>
        </dl>

        <div className="border-t border-line px-6 py-4">
          <h3 className="text-xs font-medium uppercase tracking-wide text-subtle">Delivering to</h3>
          <address className="mt-2 text-sm not-italic leading-relaxed text-muted">
            {order.shippingAddress.name}
            <br />
            {order.shippingAddress.line1}
            {order.shippingAddress.line2 && (
              <>
                <br />
                {order.shippingAddress.line2}
              </>
            )}
            <br />
            {order.shippingAddress.city}
            {order.shippingAddress.region ? `, ${order.shippingAddress.region}` : ''}{' '}
            {order.shippingAddress.postalCode}
            <br />
            {order.shippingAddress.country}
          </address>
        </div>

        {order.paymentReference && (
          <div className="border-t border-line px-6 py-4">
            <h3 className="text-xs font-medium uppercase tracking-wide text-subtle">Payment</h3>
            <p className="tabular mt-1 text-xs text-muted">{order.paymentReference}</p>
          </div>
        )}
      </Card>

      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link
          to="/"
          className="inline-flex h-11 items-center rounded-lg border border-line bg-surface px-4 text-sm font-medium transition-colors hover:bg-raised"
        >
          Continue shopping
        </Link>
        <Link
          to="/orders"
          className="inline-flex h-11 items-center rounded-lg bg-accent px-4 text-sm font-medium text-accent-ink transition-colors hover:bg-accent-hover"
        >
          View all orders
        </Link>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted">{label}</dt>
      <dd className="tabular">{value}</dd>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-7" fill="none" stroke="currentColor" strokeWidth="2.25">
      <path d="m5 13 4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-7" fill="none" stroke="currentColor" strokeWidth="2.25">
      <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-7" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" strokeLinecap="round" />
    </svg>
  );
}
